import axios from "axios";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BootstrapTable } from "react-bootstrap-table";
import { useHistory } from "react-router-dom";

import { Button } from "@material-ui/core";
import "./Crud.css";
import Loader from "./loader/Loader";

function Crud({
  keyField,
  interval,
  controlledData,
  className = "",
  initialData = [],
  title,
  navigateUrl,
  getUrl,
  getParams,
  getProcess,
  insertUrl,
  insertParams,
  insertValidate,
  updateUrl,
  updateParams,
  deleteUrl,
  deleteParams,
  deleteResultMessages,
  children,
  fake,
  getPreProcess,
  onRowClick,
  searchButton,
  onRequestData, // Nuevo prop
  pagination = true,
  openModal,
}) {
  const tableRef = useRef(null);
  const userInfo = useMemo(
    () => JSON.parse(localStorage.getItem("userInfo")),
    []
  );
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (controlledData) {
      setData(controlledData);
    }
  }, [controlledData]);

  /**
   * GET
   */
  const requestData = useCallback(() => {
    setLoading(true);
    const url = process.env.REACT_APP_API2_URL + getUrl(userInfo);

    let request;
    if (getParams) {
      request = axios.post(
        url,
        getParams instanceof Function ? getParams(userInfo) : undefined
      );
    } else {
      request = axios.get(url);
    }

    request
      .then((response) => response.data)
      .then((data) => {
        if (getPreProcess instanceof Function) {
          data = getPreProcess(data);
        }

        if (getProcess instanceof Function) {
          setData(getProcess(data));
        } else {
          setData(data);
        }
        setLoading(false);
        setDeleteLoading(false);
        if (onRequestData) onRequestData(); // Llamar al callback si está definido
      });
  }, [getUrl, userInfo, getProcess, getPreProcess, getParams, onRequestData]);

  const onSearch = useCallback(() => {
    requestData();
  }, [requestData]);

  /**
   * INSERT
   */
  const insertData = useCallback(
    (row) => {
      let params;
      try {
        params = insertParams instanceof Function
          ? insertParams(userInfo, row)
          : undefined;
      } catch (e) {
        // insertParams rejected the row (e.g. validation error already
        // alerted by the caller) — re-sync from the server so the table
        // doesn't keep the row it optimistically added locally.
        requestData();
        return;
      }
      axios
        .post(process.env.REACT_APP_API2_URL + insertUrl(userInfo, row), params)
        .then((response) => response.data)
        .then(() => requestData());
    },
    [requestData, insertUrl, insertParams, userInfo]
  );

  /**
   * UPDATE
   */
  const updateData = useCallback(
    (row) => {
      let params;
      try {
        params = updateParams instanceof Function
          ? updateParams(userInfo, row)
          : undefined;
      } catch (e) {
        requestData();
        return;
      }
      axios
        .post(process.env.REACT_APP_API2_URL + updateUrl(userInfo, row), params)
        .then((response) => response.data)
        .then(() => requestData());
    },
    [requestData, updateUrl, updateParams, userInfo]
  );

  const updateCell = {
    mode: "click",
    blurToSave: true,
    afterSaveCell: !fake ? updateData : (row) => updateUrl(userInfo, row), // a hook for after saving cell
  };

  /**
   * DELETE
   */
  const deleteData = useCallback(
    (ids, rows) => {
      setDeleteLoading(true);
      return Promise.all(
        ids.map((id, index) =>
          axios.post(
            process.env.REACT_APP_API2_URL +
              deleteUrl(userInfo, id, rows?.[index]),
            deleteParams instanceof Function
              ? deleteParams(userInfo, id, rows?.[index])
              : undefined
          )
        )
      ).then((responses) => {
        if (deleteResultMessages) {
          const errorMessages = responses
            .map((response) => deleteResultMessages[response?.data])
            .filter(Boolean);
          if (errorMessages.length > 0) {
            alert(errorMessages.join("\n"));
          }
        }
        requestData();
      });
    },
    [requestData, deleteUrl, deleteParams, userInfo, deleteResultMessages]
  );

  /**
   * Initial load
   */
  useEffect(() => {
    let intervalId = 0;

    if (getUrl) {
      if (interval) {
        intervalId = setInterval(() => {
          requestData();
        }, interval); // Will trigger every second
      }

      if (!searchButton) requestData();
    }

    return () => clearInterval(intervalId);
  }, [getUrl, requestData, interval, searchButton]);

  /**
   * Confirm
   */
  const deleteConfirm = (next, dropRowKeys) => {
    const dropRowKeysStr = dropRowKeys.join(",");
    if (
      window.confirm(
        `(It's a custom confirm)Are you sure you want to delete ${dropRowKeysStr}?`
      )
    ) {
      // If the confirmation is true, call the function that
      // continues the deletion of the record.
      next();
    }
  };

  /**
   * On Click
   */

  let history = useHistory();
  const [redirect, setRedirect] = useState(null);

  const handleOnRowClick = useCallback((row) => {
    setRedirect(row);
  }, []);

  if (redirect) {
    return <div>{history.push(navigateUrl(redirect))}</div>;
  }

  /**
   * Row select
   */
  const selectRowProp = {
    mode: "checkbox",
  };

  /**
   * TABLE OPTIONS
   */
  const options = {
    ...(openModal && {
      insertBtn: (onClick) => (
        <Button
          variant="contained"
          color="primary"
          onClick={(e) => {
            e.preventDefault();
            openModal();
          }}
          className="!rounded-xl"
        >
          New
        </Button>
      ),
    }),
    page: 1, // which page you want to show as default
    sizePerPage: 100, // which size per page you want to locate as default
    sizePerPageList: [
      {
        text: "15",
        value: 15,
      },
      {
        text: "50",
        value: 50,
      },
      {
        text: "100",
        value: 100,
      },
      {
        text: "500",
        value: 500,
      },
      {
        text: "1000",
        value: 1000,
      },
    ], // you can change the dropdown list for size per page
    pageStartIndex: 1, // where to start counting the pages
    paginationSize: 1, // the pagination bar size.
    prePage: "Prev", // Previous page button text
    nextPage: "Next", // Next page button text
    firstPage: "First", // First page button text
    lastPage: "Last", // Last page button text
    paginationShowsTotal: true, // Accept bool or function
    paginationPosition: "bottom", // default is bottom, top and both is all available
    withFirstAndLast: true, // Hide the going to First and Last page button
    afterDeleteRow: !fake ? deleteData : (row) => deleteUrl(userInfo, row), // A hook for after droping rows.
    handleConfirmDeleteRow: deleteConfirm,
    afterInsertRow: !fake ? insertData : (row) => insertUrl(userInfo, row), // A hook for after insert rows
    onAddRow:
      insertValidate instanceof Function
        ? (row, _, errorCallback) => insertValidate(data, row, errorCallback)
        : undefined, // A hook for after insert rows
    onRowClick: navigateUrl instanceof Function ? handleOnRowClick : onRowClick,
  };

  if (deleteLoading) return <Loader />;
  if (searchButton && loading) return <Loader />;
  return (
    <div id="main-div" className={`${className} !h-max relative`}>
      <h2>{title}</h2>
      {searchButton && (
        <div className="absolute right-0 z-10">
          <Button
            type="button"
            onClick={onSearch}
            color="primary"
            variant="contained"
          >
            Search
          </Button>
        </div>
      )}
      <BootstrapTable
        ref={tableRef}
        keyField={keyField}
        data={data}
        cellEdit={updateUrl instanceof Function ? updateCell : undefined}
        options={options}
        selectRow={deleteUrl instanceof Function ? selectRowProp : undefined}
        deleteRow={deleteUrl instanceof Function ? true : false}
        insertRow={insertUrl instanceof Function ? true : false}
        pagination={pagination}
        version="4"
        hover
      >
        {children}
      </BootstrapTable>
    </div>
  );
}

export default Crud;
